import React, {useState} from 'react';
import {Spinner} from "@heroui/react";
import {getErrorMessage, toastError, toastSuccess} from "../../lib/toast";
import {useMutation} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {ReactComponent as Eye} from '../../assets/icons/eye.svg';
import {ReactComponent as EyeSlash} from '../../assets/icons/eye-slash.svg';
import {ReactComponent as ProfileIcon} from '../../assets/icons/profile.svg';
import Header from "../../components/Private/Header";

const SECURITY_TIPS = [
    'رمز عبور قوی و منحصربه‌فرد برای پنل رستوران انتخاب کنید و آن را با دیگران به اشتراک نگذارید.',
    'پس از تغییر رمز، در دستگاه‌های دیگر دوباره وارد شوید.',
];

const Profile = () => {
    const [isVisible, setIsVisible] = useState({pass: false, conf: false});
    const [errors, setErrors] = useState({});
    const [feedback, setFeedback] = useState(null);

    const showFeedback = (type, message) => {
        setFeedback({type, message});
        window.scrollTo({top: 0, behavior: "smooth"});
        if (type === "success") {
            toastSuccess(message);
        } else {
            toastError(message);
        }
    };

    const {mutate, isPending} = useMutation({
        mutationFn: async (data) =>
            await request({
                url: `/restaurant/panel/pass`,
                method: "post",
                data,
            }),
        onSuccess: (data) => {
            showFeedback("success", data.message || 'رمز عبور با موفقیت تغییر کرد');
            document.getElementById("change-pass-form")?.reset();
            setErrors({});
            setIsVisible({pass: false, conf: false});
        },
        onError: (err) => {
            showFeedback("error", getErrorMessage(err));
        },
    });

    const handler = (e) => {
        e.preventDefault();

        const form = new FormData(e.target);
        const data = Object.fromEntries(form.entries());
        const newErrors = {};
        const password = data.password?.trim() || '';
        const confirmPassword = data.confirm_password?.trim() || '';

        if (!password) {
            newErrors.password = 'رمز عبور جدید را وارد کنید';
        }

        if (!confirmPassword) {
            newErrors.confirm_password = 'تکرار رمز عبور را وارد کنید';
        } else if (password !== confirmPassword) {
            newErrors.confirm_password = 'رمز عبور و تکرار آن یکسان نیستند';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        mutate({password});
    };

    return (
        <section className="rest-profile-page">
            <Header title="ویرایش پروفایل"/>

            {feedback && (
                <div
                    className={`rest-menu-save-feedback rest-menu-save-feedback--${feedback.type}`}
                    role="alert"
                >
                    {feedback.message}
                </div>
            )}

            <div className="rest-profile-hero">
                <div className="rest-profile-hero__content">
                    <span className="rest-profile-hero__eyebrow">حساب کاربری</span>
                    <h1 className="rest-profile-hero__title">تنظیمات پروفایل رستوران</h1>
                    <p className="rest-profile-hero__text">
                        امنیت ورود به پنل را با تغییر رمز عبور تقویت کنید.
                    </p>
                </div>
                <div className="rest-profile-hero__avatar" aria-hidden="true">
                    <ProfileIcon/>
                </div>
            </div>

            <div className="rest-profile-layout">
                <div className="rest-profile-main">
                    <section className="rest-profile-card">
                        <div className="rest-profile-card__header">
                            <div className="rest-profile-card__heading">
                                <span className="rest-profile-card__icon rest-profile-card__icon--security" aria-hidden="true">
                                    <EyeSlash/>
                                </span>
                                <div>
                                    <h2 className="rest-profile-card__title">تغییر رمز عبور</h2>
                                    <p className="rest-profile-card__subtitle">رمز جدید را وارد و ذخیره کنید</p>
                                </div>
                            </div>
                        </div>

                        <form
                            id="change-pass-form"
                            onSubmit={handler}
                            className="rest-profile-form"
                            noValidate
                        >
                            <div className="rest-profile-form__grid">
                                <div className="rest-profile-field">
                                    <label className="rest-profile-field__label" htmlFor="profile-password">
                                        رمز عبور جدید
                                    </label>
                                    <div className={`rest-profile-field__wrap${errors.password ? ' rest-profile-field__wrap--invalid' : ''}`}>
                                        <input
                                            id="profile-password"
                                            className="rest-profile-field__input"
                                            name="password"
                                            type={isVisible.pass ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            placeholder="رمز عبور جدید"
                                            disabled={isPending}
                                        />
                                        <button
                                            type="button"
                                            className="rest-profile-field__toggle"
                                            onClick={() => setIsVisible((prev) => ({...prev, pass: !prev.pass}))}
                                            aria-label={isVisible.pass ? 'مخفی کردن رمز' : 'نمایش رمز'}
                                        >
                                            {isVisible.pass ? <EyeSlash/> : <Eye/>}
                                        </button>
                                    </div>
                                    {errors.password ? (
                                        <p className="rest-profile-field__error">{errors.password}</p>
                                    ) : null}
                                </div>

                                <div className="rest-profile-field">
                                    <label className="rest-profile-field__label" htmlFor="profile-confirm-password">
                                        تکرار رمز عبور
                                    </label>
                                    <div className={`rest-profile-field__wrap${errors.confirm_password ? ' rest-profile-field__wrap--invalid' : ''}`}>
                                        <input
                                            id="profile-confirm-password"
                                            className="rest-profile-field__input"
                                            name="confirm_password"
                                            type={isVisible.conf ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            placeholder="رمز عبور را دوباره وارد کنید"
                                            disabled={isPending}
                                        />
                                        <button
                                            type="button"
                                            className="rest-profile-field__toggle"
                                            onClick={() => setIsVisible((prev) => ({...prev, conf: !prev.conf}))}
                                            aria-label={isVisible.conf ? 'مخفی کردن تکرار رمز' : 'نمایش تکرار رمز'}
                                        >
                                            {isVisible.conf ? <EyeSlash/> : <Eye/>}
                                        </button>
                                    </div>
                                    {errors.confirm_password ? (
                                        <p className="rest-profile-field__error">{errors.confirm_password}</p>
                                    ) : null}
                                </div>
                            </div>

                            <div className="rest-profile-form__actions">
                                <button
                                    type="submit"
                                    className="rest-action-btn rest-action-btn--primary rest-profile-form__submit"
                                    disabled={isPending}
                                >
                                    {isPending ? (
                                        <>
                                            <Spinner size="sm" color="white"/>
                                            در حال ذخیره...
                                        </>
                                    ) : 'ذخیره رمز عبور'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>

                <aside className="rest-profile-aside">
                    <section className="rest-profile-side-card">
                        <h3 className="rest-profile-side-card__title">نکات امنیتی</h3>
                        <ul className="rest-profile-tips">
                            {SECURITY_TIPS.map((tip) => (
                                <li key={tip} className="rest-profile-tips__item">{tip}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="rest-profile-side-card rest-profile-side-card--muted">
                        <h3 className="rest-profile-side-card__title">راهنمای سریع</h3>
                        <p className="rest-profile-side-card__text">
                            در این صفحه فقط تغییر رمز عبور انجام می‌شود. ویرایش منو، سفارش‌ها و گزارش‌ها از
                            منوی اصلی پنل در دسترس است.
                        </p>
                    </section>
                </aside>
            </div>
        </section>
    );
};

export default Profile;
